import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import type { EventPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function MatchCreatePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [eventCourtId, setEventCourtId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [ageGroup, setAgeGroup] = useState("");

  const [event, setEvent] = useState<EventPublic | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) return;
    api
      .getEvent(eventId)
      .then((res) => {
        setEvent(res.event);
        setEventCourtId(res.event.courts?.[0]?.id ?? "");
      })
      .catch(() => {});
  }, [eventId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!eventId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await api.createMatch(eventId, {
        event_court_id: eventCourtId || undefined,
        name,
        starts_at: new Date(startsAt).toISOString(),
        age_group: ageGroup || undefined,
      });
      navigate(`/matches/${res.match.id}`);
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Failed to create match",
      );
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="match-create" data-state={loading ? "submitting" : error ? "error" : "ready"}>
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {event && (
            <>
              <Link
                to={`/schools/${event.school_id}`}
                className="hover:underline"
              >
                {event.school_name}
              </Link>
              <span>/</span>
              <Link
                to={`/schools/${event.school_id}/events`}
                className="hover:underline"
              >
                Events
              </Link>
              <span>/</span>
              <Link to={`/events/${eventId}`} className="hover:underline">
                {event.name}
              </Link>
              <span>/</span>
            </>
          )}
          <span>New Match</span>
        </div>
        <h1 className="text-2xl font-semibold">New Match</h1>
        <p className="text-muted-foreground">
          Add a match to this event
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex max-w-md flex-col gap-4" data-region="form" data-form="match-create">
        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        <div className="flex flex-col gap-2">
          <Label htmlFor="name">Match Name</Label>
          <Input
            id="name"
            data-field="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. U14 vs Hilton College"
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="court_id">Court</Label>
          <select
            id="court_id"
            data-field="court"
            title="Court"
            value={eventCourtId}
            onChange={(e) => setEventCourtId(e.target.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            required
          >
            <option value="">Select a court...</option>
            {(event?.courts ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.is_adhoc ? " (ad-hoc)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="starts_at">Start Time</Label>
          <Input
            id="starts_at"
            data-field="starts-at"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="ends_at">End Time</Label>
          <Input
            id="ends_at"
            data-field="ends-at"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="age_group">Age Group (optional)</Label>
          <Input
            id="age_group"
            data-field="age-group"
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            placeholder="e.g. U14, 1st XV, Open"
          />
        </div>

        <div className="flex gap-3">
          <Button type="submit" data-action="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Match"}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-action="cancel"
            onClick={() => navigate(`/events/${eventId}`)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
